import { apiGet, apiPost } from "./api";
import Playlist from "../models/Playlist";
import Track from "../models/Track";

export function fetchPlaylistDetails(playlistId: number): Promise<any[]> {
    return apiGet<any[]>(`/v1/playlist/getPlaylistDetails?playlistId=${playlistId}`);
}

/** The caller's library: playlists they made, plus the ones they follow. */
export function fetchPlaylists(): Promise<Playlist[]> {
    return apiGet<Playlist[]>("/v1/playlist/getPlaylists");
}

/** Everything a given user has created, flagged with whether the caller follows each. */
export function fetchPlaylistsByUser(userId: number): Promise<Playlist[]> {
    return apiGet<Playlist[]>(`/v1/playlist/byUser?userId=${userId}`);
}

/**
 * Follows or unfollows a playlist. `follow` is sent explicitly rather than letting the
 * server toggle, so a double click can't silently invert the result. Resolves to the
 * state the server actually settled on.
 */
export async function setPlaylistFollow(playlistId: number, follow: boolean): Promise<boolean> {
    const result = await apiPost<{ following: boolean }>(
        `/v1/playlist/followPlaylist?playlistId=${playlistId}&follow=${follow}`);
    return result.following;
}

/** One follower of a playlist, as shown to that playlist's owner. */
export interface FollowerDTO {
    userId: number;
    username: string;
    profilePictureUrl?: string | null;
    contributor: boolean;
    /** An outstanding request to contribute, awaiting the owner's decision. */
    requested: boolean;
    requestedAt?: string | null;
}

/** Everyone following a playlist. Owner only - 403s for anyone else. */
export function fetchFollowers(playlistId: number): Promise<FollowerDTO[]> {
    return apiGet<FollowerDTO[]>(`/v1/playlist/followers?playlistId=${playlistId}`);
}

/**
 * Owner promotes or demotes a follower directly. Like setPlaylistFollow, the desired
 * end state is stated rather than toggled, and the server's answer is what counts.
 */
export async function setContributor(playlistId: number, userId: number, contributor: boolean): Promise<boolean> {
    const result = await apiPost<{ contributor: boolean }>(
        `/v1/playlist/setContributor?playlistId=${playlistId}&userId=${userId}&contributor=${contributor}`);
    return result.contributor;
}

/**
 * Owner drops someone from the playlist entirely - they stop following and stop being a
 * contributor. Distinct from setContributor(false), which only removes the contributor
 * badge and leaves them following.
 */
export async function removeFollower(playlistId: number, userId: number): Promise<boolean> {
    const result = await apiPost<{ removed: boolean }>(
        `/v1/playlist/removeFollower?playlistId=${playlistId}&userId=${userId}`);
    return result.removed;
}

/** A follower asks the owner for contributor access. */
export async function requestContributor(playlistId: number): Promise<boolean> {
    const result = await apiPost<{ requested: boolean }>(
        `/v1/playlist/requestContributor?playlistId=${playlistId}`);
    return result.requested;
}

/** Owner approves or denies an outstanding request. Resolves to the resulting status. */
export async function respondToContributorRequest(
    playlistId: number, userId: number, approve: boolean): Promise<boolean> {
    const result = await apiPost<{ contributor: boolean }>(
        `/v1/playlist/respondToContributorRequest?playlistId=${playlistId}&userId=${userId}&approve=${approve}`);
    return result.contributor;
}

/** Owner switches a playlist between discoverable and private. */
export async function setPlaylistPublic(playlistId: number, isPublic: boolean): Promise<boolean> {
    const result = await apiPost<{ isPublic: boolean }>(
        `/v1/playlist/setPublic?playlistId=${playlistId}&isPublic=${isPublic}`);
    return result.isPublic;
}

/**
 * The share token for a public playlist, created on first ask. `regenerate` mints a
 * new one, which is what invalidates a link that's already been sent out.
 */
export async function fetchInviteToken(playlistId: number, regenerate = false): Promise<string> {
    const result = await apiPost<{ token: string }>(
        `/v1/playlist/inviteLink?playlistId=${playlistId}&regenerate=${regenerate}`);
    return result.token;
}

/** Builds the URL the owner actually copies. */
export function inviteUrlFor(token: string): string {
    return `${window.location.origin}/invite/${token}`;
}

export interface InvitePreview {
    playlistId: number;
    playlistName: string;
    ownerUsername?: string | null;
    owned: boolean;
    following: boolean;
    contributor: boolean;
    songCount: number;
}

/** What an invite link points at, before deciding whether to join. */
export function fetchInvitePreview(token: string): Promise<InvitePreview> {
    return apiGet<InvitePreview>(`/v1/playlist/invite?token=${encodeURIComponent(token)}`);
}

/** Joins from an invite link: follows and becomes a contributor in one step. */
export function acceptInvite(token: string): Promise<{ playlistId: number; contributor: boolean }> {
    return apiPost<{ playlistId: number; contributor: boolean }>(
        `/v1/playlist/acceptInvite?token=${encodeURIComponent(token)}`);
}

/**
 * Fired after a follow/unfollow so the sidebar reloads. The search UI lives in the
 * header, several levels away from the library list with no shared ancestor holding
 * playlist state - an event keeps them decoupled rather than threading a callback
 * through every component in between.
 */
export const PLAYLISTS_CHANGED_EVENT = "shufl:playlists-changed";

export function notifyPlaylistsChanged(): void {
    window.dispatchEvent(new Event(PLAYLISTS_CHANGED_EVENT));
}

/**
 * Maps a /getPlaylistDetails row onto the shape the track table renders.
 *
 * Shared rather than inlined at each call site: the sidebar and the post-sync refresh
 * each had their own copy, and they drifted - one dropped the song and identity ids
 * that per-song state is keyed on, so the status dropdown had nothing to bind to.
 */
export function detailRowToTrack(row: any): Track {
    return {
        id: row.songId ?? row.id,
        title: row.title,
        artist: row.artist,
        imageUrl: row.imageUrl || "/assets/artist_example.png",
        addedBy: row.username || "Unknown",
        addedByUserId: row.userId,
        addedByPhotoUrl: row.profilePictureUrl || null,
        songIdentityId: row.songIdentityId,
        source: row.source,
        userDownloaded: false,
    };
}

export function createPlaylist(playlistName: string, description?: string, isPublic = false): Promise<Playlist> {
    return apiPost<Playlist>('/v1/playlist/createPlaylist', {
        playlistName,
        ...(description ? { description } : {}),
        isPublic,
    });
}

/**
 * Whether this Shufl playlist is linked to one of the caller's playlists on the
 * given provider. getLinkedPlaylist 404s when nothing's linked - that's an
 * expected outcome here, not an error, so it resolves to false rather than throwing.
 */
export async function checkPlaylistLinked(playlistId: number, provider: string): Promise<boolean> {
    try {
        await apiGet(`/v1/playlist/getLinkedPlaylist?playlistId=${playlistId}&source=${encodeURIComponent(provider)}`);
        return true;
    } catch {
        return false;
    }
}
